import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { THEMES, DEFAULT_THEME_KEY, getTheme, applyThemeToDocument } from '../config/themes';
import { systemParamAPI, authAPI } from '../api';

const THEME_PARAM_KEY = 'system_theme';
const THEME_LS_KEY = 'app_theme_key';

const ThemeContext = createContext({
  themeKey: DEFAULT_THEME_KEY,
  theme: getTheme(DEFAULT_THEME_KEY),
  themes: THEMES,
  setThemeKey: () => {},
  refreshTheme: () => {},
});

/**
 * 早期预加载：index.js 渲染前读取 localStorage 中的主题，避免首屏闪烁
 */
export const preloadThemeFromStorage = () => {
  try {
    const key = localStorage.getItem(THEME_LS_KEY) || DEFAULT_THEME_KEY;
    applyThemeToDocument(getTheme(key));
    return key;
  } catch (e) {
    applyThemeToDocument(getTheme(DEFAULT_THEME_KEY));
    return DEFAULT_THEME_KEY;
  }
};

export const ThemeProvider = ({ children }) => {
  const [themeKey, setThemeKeyState] = useState(() => {
    try {
      return localStorage.getItem(THEME_LS_KEY) || DEFAULT_THEME_KEY;
    } catch (e) {
      return DEFAULT_THEME_KEY;
    }
  });

  const theme = getTheme(themeKey);

  // 应用到 CSS 变量
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  // 启动时从后端拉取持久化的主题（以后端为准）
  // 未登录场景（登录页 / 客户自助维修门户）改走免登录的公开配置接口，避免 401
  const refreshTheme = useCallback(async () => {
    try {
      const loggedIn = !!localStorage.getItem('token');
      let value;
      if (loggedIn) {
        const res = await systemParamAPI.getParamValue(THEME_PARAM_KEY);
        // 后端返回 { success, data: 'green' } 或 data 直接是字符串
        value = res?.data ?? res;
      } else {
        const res = await authAPI.getPublicConfig();
        value = res?.data?.[THEME_PARAM_KEY];
      }
      if (value && typeof value === 'string' && THEMES[value]) {
        setThemeKeyState(value);
        localStorage.setItem(THEME_LS_KEY, value);
      }
    } catch (e) {
      // 参数不存在等，静默失败
    }
  }, []);

  useEffect(() => {
    refreshTheme();
  }, [refreshTheme]);

  // 切换主题：立刻更新本地 + 异步写入后端
  const setThemeKey = useCallback(async (key, persist = true) => {
    if (!THEMES[key]) return;
    setThemeKeyState(key);
    try { localStorage.setItem(THEME_LS_KEY, key); } catch (e) { /* ignore */ }
    if (persist) {
      try {
        await systemParamAPI.updateParamValue(THEME_PARAM_KEY, key);
      } catch (e) {
        // 后端保存失败不影响本地切换体验
        console.warn('保存主题到后端失败', e);
      }
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ themeKey, theme, themes: THEMES, setThemeKey, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
